import React, { useState, useEffect } from 'react';
import { api, API } from '../App';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { downloadAuthedBlob } from '../lib/download';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { DatePickerField } from '../components/DatePickerField';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subDays } from 'date-fns';
import {
  FileText,
  Download,
  Calendar as CalendarIcon,
  TrendingUp,
  Loader2
} from 'lucide-react';

const REPORT_TYPES = [
  { value: 'sales', label: 'Sales Report', description: 'Orders, payments, and collections' },
  { value: 'purchases', label: 'Purchases Report', description: 'Maize procurement records' },
  { value: 'expenses', label: 'Expenses Report', description: 'Operational expenses' },
  { value: 'profit', label: 'Profit Report', description: 'Revenue, costs, and net profit' },
  { value: 'factory', label: 'Factory Report', description: 'Purchases, Production, Sales by factory' },
  { value: 'batch_performance', label: 'Batch Performance', description: 'Per-batch P&L, margin, extraction' },
];

const DATE_PRESETS = [
  { label: 'Today', getValue: () => ({ start: new Date(), end: new Date() }) },
  { label: 'This Week', getValue: () => ({ start: startOfWeek(new Date(), { weekStartsOn: 1 }), end: endOfWeek(new Date(), { weekStartsOn: 1 }) }) },
  { label: 'This Month', getValue: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
  { label: 'Last 7 Days', getValue: () => ({ start: subDays(new Date(), 6), end: new Date() }) },
  { label: 'Last 30 Days', getValue: () => ({ start: subDays(new Date(), 29), end: new Date() }) },
];

export default function Reports() {
  const [reportType, setReportType] = useState('');
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [profitData, setProfitData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [purchasesData, setPurchasesData] = useState(null);
  const [expensesData, setExpensesData] = useState(null);
  const [factoryData, setFactoryData] = useState(null);  // NEW: Factory report data
  const [batchPerfData, setBatchPerfData] = useState(null);  // Batch Performance
  const [bpFilters, setBpFilters] = useState({ factory: 'all', supplier_id: 'all', pack_size: 'all', batch_status: 'all', result: 'all' });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/suppliers').then(r => setSuppliers(r.data || [])).catch(() => {}); }, []);

  const applyPreset = (preset) => {
    const { start, end } = preset.getValue();
    setStartDate(start);
    setEndDate(end);
  };

  const clearAllData = () => {
    setProfitData(null);
    setSalesData(null);
    setPurchasesData(null);
    setExpensesData(null);
    setFactoryData(null);
    setBatchPerfData(null);
  };

  const generateReport = async () => {
    if (!reportType) {
      toast.error('Please select a report type');
      return;
    }

    setLoading(true);
    clearAllData();
    
    try {
      const params = {
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd')
      };
      
      if (reportType === 'profit') {
        const response = await api.get('/profit', { params });
        setProfitData(response.data);
      } else if (reportType === 'sales') {
        const response = await api.get('/orders', { params });
        setSalesData(response.data);
      } else if (reportType === 'purchases') {
        const response = await api.get('/purchases', { params });
        setPurchasesData(response.data);
      } else if (reportType === 'expenses') {
        const response = await api.get('/expenses', { params });
        setExpensesData(response.data);
      } else if (reportType === 'factory') {
        const response = await api.get('/reports/factory', { params });
        setFactoryData(response.data);
      } else if (reportType === 'batch_performance') {
        const bpParams = { ...params };
        if (bpFilters.factory !== 'all') bpParams.factory = bpFilters.factory;
        if (bpFilters.supplier_id !== 'all') bpParams.supplier_id = bpFilters.supplier_id;
        if (bpFilters.pack_size !== 'all') bpParams.pack_size = bpFilters.pack_size;
        if (bpFilters.batch_status !== 'all') bpParams.batch_status = bpFilters.batch_status;
        if (bpFilters.result !== 'all') bpParams.result = bpFilters.result;
        const response = await api.get('/reports/batch-performance', { params: bpParams });
        setBatchPerfData(response.data);
      }
      toast.success('Report generated');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!reportType) {
      toast.error('Please select a report type');
      return;
    }

    try {
      if (reportType === 'batch_performance') {
        const params = { start_date: format(startDate, 'yyyy-MM-dd'), end_date: format(endDate, 'yyyy-MM-dd') };
        if (bpFilters.factory !== 'all') params.factory = bpFilters.factory;
        if (bpFilters.supplier_id !== 'all') params.supplier_id = bpFilters.supplier_id;
        if (bpFilters.pack_size !== 'all') params.pack_size = bpFilters.pack_size;
        if (bpFilters.batch_status !== 'all') params.batch_status = bpFilters.batch_status;
        if (bpFilters.result !== 'all') params.result = bpFilters.result;
        await downloadAuthedBlob(
          '/reports/batch-performance/csv',
          `Batch_Performance_${params.start_date}_${params.end_date}.csv`,
          { params }
        );
        return;
      }
      const url = `${API}/reports/export/excel?report_type=${reportType}&start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const downloadBatchPerfPdf = async () => {
    const params = { start_date: format(startDate, 'yyyy-MM-dd'), end_date: format(endDate, 'yyyy-MM-dd') };
    if (bpFilters.factory !== 'all') params.factory = bpFilters.factory;
    if (bpFilters.supplier_id !== 'all') params.supplier_id = bpFilters.supplier_id;
    if (bpFilters.pack_size !== 'all') params.pack_size = bpFilters.pack_size;
    if (bpFilters.batch_status !== 'all') params.batch_status = bpFilters.batch_status;
    if (bpFilters.result !== 'all') params.result = bpFilters.result;
    await downloadAuthedBlob(
      '/reports/batch-performance/pdf',
      `Batch_Performance_${params.start_date}_${params.end_date}.pdf`,
      { params }
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Reports</h1>
        <p className="text-slate-500 mt-1">Generate and export business reports</p>
      </div>

      {/* Report Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Report Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Report Type */}
          <div className="space-y-2">
            <Label>Report Type</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {REPORT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => { setReportType(type.value); clearAllData(); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    reportType === type.value
                      ? 'border-[#1B4D3E] bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`report-type-${type.value}`}
                >
                  <p className="font-medium text-slate-900">{type.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <Label>Date Range</Label>
            <div className="flex flex-wrap gap-2 mb-3">
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(preset)}
                  data-testid={`preset-${preset.label.toLowerCase().replace(' ', '-')}`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <div className="flex gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Start Date</Label>
                <DatePickerField
                  date={startDate}
                  onDateChange={setStartDate}
                  buttonClassName="w-48 justify-start text-left font-normal"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">End Date</Label>
                <DatePickerField
                  date={endDate}
                  onDateChange={setEndDate}
                  buttonClassName="w-48 justify-start text-left font-normal"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 flex-wrap">
            <Button
              onClick={generateReport}
              className="btn-primary gap-2"
              disabled={loading || !reportType}
              data-testid="generate-report-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Generate Report
            </Button>
            <Button
              onClick={downloadExcel}
              variant="outline"
              className="gap-2"
              disabled={!reportType}
              data-testid="download-excel-btn"
            >
              <Download className="h-4 w-4" />
              {reportType === 'batch_performance' ? 'Download CSV' : 'Download Excel'}
            </Button>
            {reportType === 'batch_performance' && (
              <Button onClick={downloadBatchPerfPdf} variant="outline" className="gap-2" data-testid="download-bp-pdf-btn">
                <Download className="h-4 w-4" /> Download PDF
              </Button>
            )}
          </div>

          {/* Batch Performance Filters */}
          {reportType === 'batch_performance' && (
            <div className="pt-4 mt-2 border-t space-y-2">
              <Label>Batch Performance Filters</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <div className="space-y-1">
                  <Label className="text-xs">Factory</Label>
                  <Select value={bpFilters.factory} onValueChange={(v) => setBpFilters({ ...bpFilters, factory: v })}>
                    <SelectTrigger data-testid="bp-factory"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                      <SelectItem value="Githunguri">Githunguri</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Supplier</Label>
                  <Select value={bpFilters.supplier_id} onValueChange={(v) => setBpFilters({ ...bpFilters, supplier_id: v })}>
                    <SelectTrigger data-testid="bp-supplier"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="all">All</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Pack Size</Label>
                  <Select value={bpFilters.pack_size} onValueChange={(v) => setBpFilters({ ...bpFilters, pack_size: v })}>
                    <SelectTrigger data-testid="bp-packsize"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="0.5kg">0.5kg</SelectItem>
                      <SelectItem value="1kg">1kg</SelectItem>
                      <SelectItem value="2kg">2kg</SelectItem>
                      <SelectItem value="50kg">50kg</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={bpFilters.batch_status} onValueChange={(v) => setBpFilters({ ...bpFilters, batch_status: v })}>
                    <SelectTrigger data-testid="bp-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="Ready to Close">Ready to Close</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Result</Label>
                  <Select value={bpFilters.result} onValueChange={(v) => setBpFilters({ ...bpFilters, result: v })}>
                    <SelectTrigger data-testid="bp-result"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="Profit">Profit</SelectItem>
                      <SelectItem value="Break-even">Break-even</SelectItem>
                      <SelectItem value="Loss">Loss</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profit Report Display */}
      {profitData && (
        <Card data-testid="profit-report-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading">Profit Report</CardTitle>
              <span className="text-sm text-slate-500">
                {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-xl">
                <p className="text-sm text-green-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-700" data-testid="profit-revenue">
                  {formatCurrency(profitData.revenue?.total)}
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-xl">
                <p className="text-sm text-red-600">Total Costs</p>
                <p className="text-2xl font-bold text-red-700" data-testid="profit-costs">
                  {formatCurrency(profitData.costs?.total)}
                </p>
              </div>
              <div className={`p-4 rounded-xl ${profitData.net_profit >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                <p className={`text-sm ${profitData.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Net Profit</p>
                <p className={`text-2xl font-bold ${profitData.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="profit-net">
                  {formatCurrency(profitData.net_profit)}
                </p>
              </div>
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-2 gap-6">
              {/* Revenue */}
              <div className="space-y-3">
                <h3 className="font-heading font-semibold text-green-700 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Revenue Breakdown
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Finished Goods Sales</span>
                    <span className="font-medium">{formatCurrency(profitData.revenue?.finished_goods)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Byproduct Sales</span>
                    <span className="font-medium">{formatCurrency(profitData.revenue?.byproducts)}</span>
                  </div>
                </div>
              </div>

              {/* Costs */}
              <div className="space-y-3">
                <h3 className="font-heading font-semibold text-red-700 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 rotate-180" />
                  Costs Breakdown
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Maize Purchases</span>
                    <span className="font-medium">{formatCurrency(profitData.costs?.purchases)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Packaging Materials</span>
                    <span className="font-medium">{formatCurrency(profitData.costs?.packaging)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Expenses</span>
                    <span className="font-medium">{formatCurrency(profitData.costs?.expenses)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales Report Display */}
      {salesData && (
        <Card data-testid="sales-report-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading">Sales Report</CardTitle>
              <span className="text-sm text-slate-500">
                {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Row */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600">Total Orders</p>
                <p className="text-xl font-bold text-blue-700">{salesData.length}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600">Total Sales</p>
                <p className="text-xl font-bold text-green-700">
                  {formatCurrency(salesData.reduce((sum, o) => sum + (o.order_total || 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-600">Total Paid</p>
                <p className="text-xl font-bold text-amber-700">
                  {formatCurrency(salesData.reduce((sum, o) => sum + (o.paid_amount || 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600">Outstanding</p>
                <p className="text-xl font-bold text-red-700">
                  {formatCurrency(salesData.reduce((sum, o) => sum + (o.balance || 0), 0))}
                </p>
              </div>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B4D3E] text-white">
                  <TableHead className="text-white">Date</TableHead>
                  <TableHead className="text-white">Order No</TableHead>
                  <TableHead className="text-white">Customer</TableHead>
                  <TableHead className="text-white">Agent</TableHead>
                  <TableHead className="text-white">Destination</TableHead>
                  <TableHead className="text-white text-right">Total</TableHead>
                  <TableHead className="text-white text-right">Paid</TableHead>
                  <TableHead className="text-white text-right">Balance</TableHead>
                  <TableHead className="text-white text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      No sales found in this period
                    </TableCell>
                  </TableRow>
                ) : (
                  salesData.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.date}</TableCell>
                      <TableCell className="font-medium text-[#1B4D3E]">{order.order_number}</TableCell>
                      <TableCell>{order.customer_name}</TableCell>
                      <TableCell>{order.agent_name || '-'}</TableCell>
                      <TableCell>{order.destination || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(order.order_total)}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(order.paid_amount)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(order.balance)}</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          order.status === 'Paid' ? 'bg-green-100 text-green-700' :
                          order.status === 'Part Paid' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {order.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Purchases Report Display */}
      {purchasesData && (
        <Card data-testid="purchases-report-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading">Purchases Report</CardTitle>
              <span className="text-sm text-slate-500">
                {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Row */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600">Total Batches</p>
                <p className="text-xl font-bold text-blue-700">{purchasesData.length}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-600">Total Kg Received</p>
                <p className="text-xl font-bold text-amber-700">
                  {purchasesData.reduce((sum, p) => sum + (p.kg_received || 0), 0).toLocaleString()} kg
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600">Total Cost</p>
                <p className="text-xl font-bold text-slate-700">
                  {formatCurrency(purchasesData.reduce((sum, p) => sum + (p.total_cost || 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600">Outstanding</p>
                <p className="text-xl font-bold text-red-700">
                  {formatCurrency(purchasesData.reduce((sum, p) => sum + (p.balance || 0), 0))}
                </p>
              </div>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B4D3E] text-white">
                  <TableHead className="text-white">Date</TableHead>
                  <TableHead className="text-white">Batch No</TableHead>
                  <TableHead className="text-white">Supplier</TableHead>
                  <TableHead className="text-white text-center">Status</TableHead>
                  <TableHead className="text-white text-right">Kg Received</TableHead>
                  <TableHead className="text-white text-right">Total Cost</TableHead>
                  <TableHead className="text-white text-right">Paid</TableHead>
                  <TableHead className="text-white text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchasesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No purchases found in this period
                    </TableCell>
                  </TableRow>
                ) : (
                  purchasesData.map((purchase) => (
                    <TableRow key={purchase.id}>
                      <TableCell>{purchase.date}</TableCell>
                      <TableCell className="font-bold text-[#1B4D3E]">{purchase.batch_no}</TableCell>
                      <TableCell>{purchase.supplier_name}</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          purchase.batch_status === 'Processed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {purchase.batch_status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{purchase.kg_received.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatCurrency(purchase.total_cost)}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(purchase.paid_amount)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(purchase.balance)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Expenses Report Display */}
      {expensesData && (
        <Card data-testid="expenses-report-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading">Expenses Report</CardTitle>
              <span className="text-sm text-slate-500">
                {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Row */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600">Total Entries</p>
                <p className="text-xl font-bold text-blue-700">{expensesData.length}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600">Total Amount</p>
                <p className="text-xl font-bold text-red-700">
                  {formatCurrency(expensesData.reduce((sum, e) => sum + (e.amount || 0), 0))}
                </p>
              </div>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B4D3E] text-white">
                  <TableHead className="text-white">Date</TableHead>
                  <TableHead className="text-white">Category</TableHead>
                  <TableHead className="text-white text-right">Amount</TableHead>
                  <TableHead className="text-white">Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expensesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      No expenses found in this period
                    </TableCell>
                  </TableRow>
                ) : (
                  expensesData.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>{expense.date}</TableCell>
                      <TableCell className="font-medium">{expense.category}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(expense.amount)}</TableCell>
                      <TableCell className="text-slate-600">{expense.remarks || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Factory Report Display */}
      {factoryData && (
        <Card data-testid="factory-report-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading">Factory Report</CardTitle>
              <span className="text-sm text-slate-500">
                {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Purchases by Factory */}
            <div>
              <h3 className="font-heading font-semibold text-blue-700 mb-3">Purchases by Factory</h3>
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-50">
                    <TableHead>Factory</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">Kg Received</TableHead>
                    <TableHead className="text-right">Total Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {['Ndumberi', 'Githunguri'].map(factory => (
                    <TableRow key={factory}>
                      <TableCell className="font-medium">{factory}</TableCell>
                      <TableCell className="text-right">{factoryData.factories?.[factory]?.purchases?.count || 0}</TableCell>
                      <TableCell className="text-right">{(factoryData.factories?.[factory]?.purchases?.kg || 0).toLocaleString()} kg</TableCell>
                      <TableCell className="text-right">{formatCurrency(factoryData.factories?.[factory]?.purchases?.cost || 0)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">{factoryData.totals?.purchases?.count || 0}</TableCell>
                    <TableCell className="text-right">{(factoryData.totals?.purchases?.kg || 0).toLocaleString()} kg</TableCell>
                    <TableCell className="text-right">{formatCurrency(factoryData.totals?.purchases?.cost || 0)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Production by Factory */}
            <div>
              <h3 className="font-heading font-semibold text-emerald-700 mb-3">Production by Factory</h3>
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-50">
                    <TableHead>Factory</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">Flour (kg)</TableHead>
                    <TableHead className="text-right">Byproducts (kg)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {['Ndumberi', 'Githunguri'].map(factory => (
                    <TableRow key={factory}>
                      <TableCell className="font-medium">{factory}</TableCell>
                      <TableCell className="text-right">{factoryData.factories?.[factory]?.production?.count || 0}</TableCell>
                      <TableCell className="text-right">{(factoryData.factories?.[factory]?.production?.flour_kg || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{(factoryData.factories?.[factory]?.production?.byproducts_kg || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">{factoryData.totals?.production?.count || 0}</TableCell>
                    <TableCell className="text-right">{(factoryData.totals?.production?.flour_kg || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right">{(factoryData.totals?.production?.byproducts_kg || 0).toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Dispatched Sales by Factory */}
            <div>
              <h3 className="font-heading font-semibold text-amber-700 mb-3">Dispatched Sales by Factory</h3>
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50">
                    <TableHead>Factory</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Bales</TableHead>
                    <TableHead className="text-right">Byproducts (kg)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {['Ndumberi', 'Githunguri'].map(factory => (
                    <TableRow key={factory}>
                      <TableCell className="font-medium">{factory}</TableCell>
                      <TableCell className="text-right">{Math.round(factoryData.factories?.[factory]?.dispatched_sales?.count || 0)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(factoryData.factories?.[factory]?.dispatched_sales?.total || 0)}</TableCell>
                      <TableCell className="text-right">{Math.round(factoryData.factories?.[factory]?.dispatched_sales?.bales || 0)}</TableCell>
                      <TableCell className="text-right">{(factoryData.factories?.[factory]?.dispatched_sales?.byproducts_kg || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">{Math.round(factoryData.totals?.dispatched_sales?.count || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(factoryData.totals?.dispatched_sales?.total || 0)}</TableCell>
                    <TableCell className="text-right">{Math.round(factoryData.totals?.dispatched_sales?.bales || 0)}</TableCell>
                    <TableCell className="text-right">{(factoryData.totals?.dispatched_sales?.byproducts_kg || 0).toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Batch Performance Display */}
      {batchPerfData && (
        <Card data-testid="batch-performance-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Batch Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Total Batches', batchPerfData.summary.total_batches],
                ['Total Kg Maize', formatCurrency(batchPerfData.summary.total_kg_purchased).replace('KES', '').trim() + ' kg'],
                ['Total Maize Cost', formatCurrency(batchPerfData.summary.total_maize_cost)],
                ['Total OpEx', formatCurrency(batchPerfData.summary.total_operating_expenses)],
                ['Total Cost', formatCurrency(batchPerfData.summary.total_cost)],
                ['Total Revenue', formatCurrency(batchPerfData.summary.total_revenue)],
                ['Total Profit / Loss', formatCurrency(batchPerfData.summary.total_profit_loss)],
                ['Avg Margin %', `${batchPerfData.summary.avg_profit_margin_pct.toFixed(2)}%`],
                ['Avg Extraction %', `${batchPerfData.summary.avg_extraction_pct.toFixed(2)}%`],
                ['Profit / Bale Eq', formatCurrency(batchPerfData.summary.avg_profit_per_bale_equivalent)],
              ].map(([k, v]) => (
                <div key={k} className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500">{k}</div>
                  <div className="text-base font-semibold text-slate-900" data-testid={`bp-summary-${k.replace(/\s+/g,'-').toLowerCase()}`}>{v}</div>
                </div>
              ))}
            </div>
            {(batchPerfData.summary.best_batch || batchPerfData.summary.worst_batch) && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                {batchPerfData.summary.best_batch && (
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="text-xs text-emerald-700">Best Performing</div>
                    <div className="font-semibold text-emerald-900">{batchPerfData.summary.best_batch.batch_no} · {formatCurrency(batchPerfData.summary.best_batch.profit_loss)}</div>
                  </div>
                )}
                {batchPerfData.summary.worst_batch && (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <div className="text-xs text-red-700">Lowest Performing</div>
                    <div className="font-semibold text-red-900">{batchPerfData.summary.worst_batch.batch_no} · {formatCurrency(batchPerfData.summary.worst_batch.profit_loss)}</div>
                  </div>
                )}
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#1B4D3E] text-white">
                    <TableHead className="text-white">Batch No</TableHead>
                    <TableHead className="text-white">Date</TableHead>
                    <TableHead className="text-white">Supplier</TableHead>
                    <TableHead className="text-white">Factory</TableHead>
                    <TableHead className="text-white text-right">Kg</TableHead>
                    <TableHead className="text-white text-right">Maize Cost</TableHead>
                    <TableHead className="text-white text-right">Flour Kg</TableHead>
                    <TableHead className="text-white text-right">Ext %</TableHead>
                    <TableHead className="text-white text-right">Revenue</TableHead>
                    <TableHead className="text-white text-right">Cost</TableHead>
                    <TableHead className="text-white text-right">P/L</TableHead>
                    <TableHead className="text-white text-right">Margin %</TableHead>
                    <TableHead className="text-white text-center">Status</TableHead>
                    <TableHead className="text-white text-center">Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batchPerfData.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={14} className="text-center py-8 text-slate-500">No batches match filters</TableCell></TableRow>
                  ) : batchPerfData.rows.map(r => (
                    <TableRow key={r.purchase_id} data-testid={`bp-row-${r.purchase_id}`}>
                      <TableCell><Link to={`/batches/${r.purchase_id}`} className="text-emerald-700 font-medium hover:underline">{r.batch_no}</Link></TableCell>
                      <TableCell>{r.purchase_date}</TableCell>
                      <TableCell className="max-w-[140px] truncate">{r.supplier || '-'}</TableCell>
                      <TableCell>{r.factory || '-'}</TableCell>
                      <TableCell className="text-right">{Number(r.kg_purchased || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.maize_cost)}</TableCell>
                      <TableCell className="text-right">{Number(r.flour_kg || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{r.extraction_pct.toFixed(1)}%</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.total_revenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.total_cost)}</TableCell>
                      <TableCell className={`text-right font-semibold ${r.profit_loss >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatCurrency(r.profit_loss)}</TableCell>
                      <TableCell className="text-right">{r.margin_pct.toFixed(1)}%</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${r.status === 'Closed' ? 'bg-slate-200 text-slate-700' : r.status === 'Ready to Close' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{r.status}</span>
                        <div className="text-[10px] text-slate-400">{r.is_final ? 'Final' : 'Provisional'}</div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${r.result === 'Profit' ? 'bg-emerald-100 text-emerald-700' : r.result === 'Loss' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>{r.result}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
